// @flow
import React from 'react';
import { bindActionCreators, compose } from 'redux';
import { capitalize } from 'lodash';
import { connect } from 'react-redux';

import Button from 'components/Button';
import Glyph from 'components/Glyph';
import GroupAvatar from 'groups/components/GroupAvatar';
import GroupChatAvatar from 'chat/components/GroupChatAvatar';
import MediaObject from 'components/MediaObject';
import OrgAvatar from 'organizations/components/OrgAvatar';
import Row from 'components/Row';
import Text from 'components/Text';
import isNumber from 'lodash/isNumber';
import UserAvatar from 'users/components/UserAvatar';

import { ComposerContext } from './ComposerContext';

import { RECIPIENT_TYPES, selectRecipient } from 'reducers/roleBasedMessagingSearch';
import type { Recipient, MessageFilterType } from 'reducers/roleBasedMessagingSearch';

type Props = {
  selection: Recipient,
  count: number,
  // redux
  onSelectRecipient: (recipient: Recipient, filter: ?MessageFilterType) => *,
};

type State = {
  hovered: boolean,
};

class RecipientSelectionItem extends React.Component<Props, State> {
  state = {
    hovered: false,
  };

  handleRemoval = () => {
    const { selection } = this.props;
    if (!selection) {
      return null;
    }
    return this.props.onSelectRecipient(selection, null);
  };

  renderSelectionItem = (options: {
    avatar: *,
    primaryText: string,
    secondaryText?: string,
    count?: number,
    eventKey: string,
    eventProperties?: Object,
  }) => {
    const { avatar, primaryText, secondaryText, count, eventKey, eventProperties } = options;
    return (
      <ComposerContext.Consumer>
        {composerContext => (
          <li
            className="SelectFromList-selectionItem"
            onMouseOver={() => this.setState({ hovered: true })}
            onMouseLeave={() => this.setState({ hovered: false })}>
            <Row size="small">
              <MediaObject figure="right" gutter="xsmall" center={!secondaryText}>
                <MediaObject gutter="small" center={!secondaryText}>
                  {avatar}
                  <div>
                    <Text size="xsmall" element="div" className="SelectFromList-selectionItem-name">
                      {primaryText}
                    </Text>
                    {secondaryText && (
                      <Text size="xxsmall" element="div" color="eclipse">
                        {secondaryText}
                      </Text>
                    )}
                  </div>
                </MediaObject>
                <div className="SelectFromList-selectionItem-figure">
                  {this.state.hovered ? (
                    <Button
                      aria-label={`Remove ${primaryText} from selection`}
                      onClick={this.handleRemoval}
                      level="icon"
                      data-test="close-btn"
                      eventKey={eventKey}
                      eventProperties={{
                        ...composerContext.getUIEventContext(),
                        eventProperties,
                      }}>
                      <Glyph name="close-circle" color="eclipse" size="medium" solo />
                    </Button>
                  ) : (
                    <Text size="xsmall" color="eclipse">
                      {isNumber(count) ? count : ''}
                    </Text>
                  )}
                </div>
              </MediaObject>
            </Row>
          </li>
        )}
      </ComposerContext.Consumer>
    );
  };

  render() {
    const { selection, count } = this.props;
    if (!selection) {
      return null;
    }
    if (selection.type === RECIPIENT_TYPES.ORGANIZATION_RECIPIENT) {
      const { organization } = selection;
      const filterValues = selection.selectedFilters
        ? selection.selectedFilters.map(filter => filter.value.label)
        : [];
      return this.renderSelectionItem({
        avatar: <OrgAvatar size="xsmall" />,
        primaryText: organization.name,
        secondaryText: filterValues.join(', '),
        count,
        eventKey: 'announcements.share_to.organization.selected_list.unselect.click',
        eventProperties: { organization_uuid: organization.uuid },
      });
    }
    if (selection.type === RECIPIENT_TYPES.GROUP_RECIPIENT) {
      const { group } = selection;
      const filterValues = selection.selectedFilters
        ? selection.selectedFilters.map(filter => filter.value.label)
        : [];
      return this.renderSelectionItem({
        /* $FlowFixMe - group is bad here because lack of type safety around it. */
        avatar: <GroupAvatar group={group} size="xsmall" />,
        primaryText: group.className,
        secondaryText: filterValues.join(', '),
        count,
        eventKey: 'announcements.share_to.group.selected_list.unselect.click',
      });
    }
    if (selection.type === RECIPIENT_TYPES.GROUP_CHAT_RECIPIENT) {
      const { chat } = selection;
      return this.renderSelectionItem({
        avatar: <GroupChatAvatar size="xsmall" />,
        primaryText: chat.getTitle(),
        secondaryText: `${count} people`,
        count,
        eventKey: 'announcements.share_to.group_chat.selected_list.unselect.click',
      });
    }
    if (selection.type === RECIPIENT_TYPES.INDIVIDUAL_RECIPIENT) {
      const { member } = selection;
      return this.renderSelectionItem({
        avatar: <UserAvatar user={member.getUser().toJSON()} size="xsmall" hideBadge />,
        primaryText: member.getName(),
        secondaryText: capitalize(member.getRole()),
        count,
        eventKey: 'announcements.share_to.individual.selected_list.unselect.click',
      });
    }
    return null;
  }
}

const mapDispatchToProps = (dispatch: Function) => {
  return bindActionCreators(
    {
      onSelectRecipient: selectRecipient,
    },
    dispatch
  );
};

export { RecipientSelectionItem };

export default compose(
  connect(
    undefined,
    mapDispatchToProps
  )
)(RecipientSelectionItem);
